/**
 * Polling the device list.
 *
 * Polling rather than a WebSocket, deliberately. The list tolerates staleness by
 * design — an offline device shows its last known state — so a socket would buy
 * nothing today while costing reconnect handling, backoff, and auth-on-upgrade,
 * all of which get rewritten when live view arrives in Phase 5.
 *
 * The rule this hook exists to enforce: a failed refresh NEVER discards the data
 * already on screen. It downgrades confidence, not content.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchDevices, ApiError, type ApiDevice } from './api.ts';
import {
  DEVICE_POLL_INTERVAL_MS,
  shouldPoll,
  type DeviceSummary,
  type FetchState,
} from './deviceViewModel.ts';

/** Maps the API shape onto the view model's, keeping timestamps as epoch ms. */
function toSummary(device: ApiDevice): DeviceSummary {
  return {
    id: device.id,
    name: device.name,
    siteName: device.site_name,
    status: device.status,
    lastSeenAt: device.last_seen_at ? new Date(device.last_seen_at).getTime() : null,
    cameras: device.cameras.map((camera) => ({
      id: camera.id,
      name: camera.name,
      // The API reports what the device last said; anything unrecognised is
      // shown as unknown rather than dropped.
      state: (['unknown', 'connecting', 'streaming', 'offline', 'error'] as const).includes(
        camera.state as 'unknown',
      )
        ? (camera.state as DeviceSummary['cameras'][number]['state'])
        : 'unknown',
      oldestSegmentAt: null,
      newestSegmentAt: null,
    })),
  };
}

export interface UseDevices {
  devices: DeviceSummary[];
  fetchState: FetchState;
  /** Set when the session is no longer valid, so the app can show the login form. */
  unauthenticated: boolean;
  refresh: () => void;
}

export function useDevices(enabled: boolean): UseDevices {
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'loading' });
  const [unauthenticated, setUnauthenticated] = useState(false);

  // Tracked in a ref so a failure can report when data was last good without
  // that value being a render dependency.
  const lastSucceededAt = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await fetchDevices();
      lastSucceededAt.current = Date.now();
      setDevices(result.map(toSummary));
      setFetchState({ status: 'ok', fetchedAt: lastSucceededAt.current });
      setUnauthenticated(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUnauthenticated(true);
        return;
      }
      /*
       * Note what is absent: setDevices is not called. The list keeps whatever it
       * last had, and only the fetch state changes. This is the requirement --
       * "this camera was streaming eleven minutes ago" is actionable, "Error" is
       * not.
       */
      setFetchState({
        status: 'failed',
        fetchedAt: Date.now(),
        lastSucceededAt: lastSucceededAt.current,
      });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const run = (): void => {
      if (cancelled) return;
      void load();
    };

    run();

    let timer = window.setInterval(() => {
      // Paused while hidden: a background tab polling every 15s for hours costs
      // the operator's battery and the server's capacity for a screen nobody is
      // looking at.
      if (shouldPoll(document.hidden)) run();
    }, DEVICE_POLL_INTERVAL_MS);

    // Coming back to the tab refreshes immediately rather than waiting out the
    // remainder of an interval, so returning never shows visibly stale data.
    const onVisibility = (): void => {
      if (!document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      timer = 0;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, load]);

  return { devices, fetchState, unauthenticated, refresh: () => void load() };
}
